import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useApi } from '@/shared/api';
import { problemText } from '@/shared/api/problemText';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { PageHeader } from '@/shared/components';
import {
  SurfaceLoadError,
  SurfaceLoading,
  SurfaceStaleHint,
} from '@/shared/components/SurfaceStates';
import { CleaningScopeRequired } from '../CleaningScopeRequired';
import { todayInTimeZone } from '../calendar/cleaningCalendarHelpers';
import { useCleaningScope } from '../useCleaningScope';
import {
  CompletionReportFilters,
  type ReportPeriod,
  type ReportSearch,
} from './CompletionReportFilters';
import { CompletionReportResults } from './CompletionReportResults';
import { monthRange, periodLabel } from './completionReportModel';

const CARD = 'border-hairline rounded-tile overflow-hidden border bg-white';

/**
 * Auswertung abgeschlossener Reinigungen (Basis-Stil): Zeitraum, Objekt, Apartment und Person als
 * Filter, dann Summen, Diagramm je Apartment, Tabelle und Detail. Nur Serverwerte.
 */
export function CleaningCompletionReportPage() {
  const api = useApi();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_admin/cleaning/reports' });
  const scope = useCleaningScope();
  const timezone = scope.group?.timezone ?? scope.groups[0]?.timezone;
  const defaultRange = monthRange(timezone ? todayInTimeZone(timezone) : '1970-01-01');
  const from = search.from ?? defaultRange.from;
  const to = search.to ?? defaultRange.to;
  const period: ReportPeriod = search.period ?? 'month';
  const selectedObject = scope.groups.find((item) => item.id === search.objectId);
  const propertyIds = search.propertyId
    ? [search.propertyId]
    : selectedObject?.apartments.map((apartment) => apartment.id);
  const report = useQuery({
    queryKey: [
      'cleaning',
      'completion-report',
      from,
      to,
      propertyIds,
      search.assignedToUserId,
      search.selectedPropertyId,
    ],
    queryFn: () =>
      api.cleaning.completionReport({
        from,
        to,
        propertyIds,
        assignedToUserId: search.assignedToUserId,
        selectedPropertyId: search.selectedPropertyId,
      }),
    enabled: Boolean(timezone),
  });
  const problem = useApiProblem(report.error);
  const patch = (next: Partial<ReportSearch>) => {
    const { period: nextPeriod, ...rest } = next;
    return void navigate({
      to: '/cleaning/reports',
      search: {
        ...search,
        ...rest,
        period:
          nextPeriod === undefined ? search.period : nextPeriod === 'range' ? 'range' : undefined,
      },
    });
  };

  if (!timezone)
    return (
      <CleaningScopeRequired
        groups={scope.groups}
        loading={scope.isLoading}
        error={scope.isError}
        select={scope.setGroupId}
        retry={scope.retry}
      />
    );
  return (
    <div className="mx-auto flex w-full max-w-[96rem] flex-col gap-4">
      <PageHeader
        className="mb-0"
        title="Auswertung"
        description={`Abgeschlossene Reinigungen · ${periodLabel(from, to, period)}`}
      />
      <CompletionReportFilters
        search={{ ...search, from, to, period }}
        groups={scope.groups}
        report={report.data}
        onPatch={patch}
      />
      {report.isPending && report.fetchStatus === 'paused' ? (
        <p role="status" className={`${CARD} text-ink-soft p-5 text-sm`}>
          Keine Verbindung. Die Auswertung lädt, sobald sie zurück ist.
        </p>
      ) : report.isPending ? (
        <div className={CARD}>
          <SurfaceLoading label="Auswertung wird geladen" rows={4} />
        </div>
      ) : !report.data ? (
        <div className={CARD}>
          <SurfaceLoadError
            message={problemText(problem, 'Die Auswertung konnte nicht geladen werden.')}
            onRetry={() => void report.refetch()}
            retrying={report.isFetching}
          />
        </div>
      ) : (
        <>
          {report.isError && (
            <div className={CARD}>
              <SurfaceStaleHint
                onRetry={() => void report.refetch()}
                retrying={report.isFetching}
              />
            </div>
          )}
          <CompletionReportResults
            report={report.data}
            from={from}
            to={to}
            onSelectApartment={(propertyId) => patch({ selectedPropertyId: propertyId })}
          />
        </>
      )}
    </div>
  );
}
